/* Client-side bug capture (BUG_TRACKING_AND_FIXING.md §2).
 *
 * Captures global JS errors, unhandled promise rejections, intercepted console
 * error/warn, React render crashes (via BugErrorBoundary), and API 4xx/5xx
 * (via api.js), then batches them to our own server at /server/telemetry/bug-report.
 * The server holds the BUG_LRS credentials and pseudonymizes the actor — the client
 * NEVER sees creds and never sends an email.
 *
 * Three guarantees (do not regress):
 *   1. Never crashes the app — every path is wrapped in silent try/catch.
 *   2. The '[BugReporter]' prefix guard keeps the console intercept from self-feeding.
 *   3. No credentials in the payload — we talk only to our own origin.
 */

const ENDPOINT = "/server/telemetry/bug-report";
const PREFIX = "[BugReporter]";
const MAX_QUEUE = 20;        // drop further reports once full
const DEDUP_TTL = 60_000;    // 1-minute TTL by `${source}:${message}`
const FLUSH_MS = 3_000;      // flush queue every 3s while the page is open

const queue = [];
const dedup = new Map();
let installed = false;

function deduped(source, message) {
  try {
    const key = `${source}:${message}`;
    const now = Date.now();
    const until = dedup.get(key);
    if (until && until > now) return true;
    dedup.set(key, now + DEDUP_TTL);
    return false;
  } catch { return false; }
}

function enqueue(report) {
  try {
    const source = report.source || "client";
    const message = String(report.message || "unknown").slice(0, 8000);
    if (deduped(source, message)) return;
    if (queue.length >= MAX_QUEUE) return;
    queue.push({
      source,
      message,
      stack: report.stack ? String(report.stack).slice(0, 4000) : undefined,
      component: report.component,
      route: report.route || safeRoute(),
      severity: report.severity || "error",
      context: {
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        url: safeUrl(),
        timestamp: new Date().toISOString(),
        ...(report.context || {}),
      },
    });
  } catch { /* never throw */ }
}

function safeRoute() { try { return location.pathname + location.search; } catch { return ""; } }
function safeUrl() { try { return location.href; } catch { return ""; } }

function flush() {
  if (!queue.length) return;
  const batch = queue.splice(0, queue.length);
  for (const report of batch) {
    try {
      fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(report),
        credentials: "same-origin",  // carry the session cookie; never an auth token
        keepalive: true,
      }).catch(() => {});
    } catch { /* never throw */ }
  }
}

function flushBeacon() {
  if (!queue.length) return;
  const batch = queue.splice(0, queue.length);
  for (const report of batch) {
    try {
      // sendBeacon survives tab close / navigation where fetch is cancelled (esp. mobile,
      // where 'beforeunload' never fires). Falls back to keepalive fetch if unavailable.
      const body = new Blob([JSON.stringify(report)], { type: "application/json" });
      if (!(navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, body))) {
        fetch(ENDPOINT, { method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify(report), credentials: "same-origin", keepalive: true }).catch(() => {});
      }
    } catch { /* never throw */ }
  }
}

/* Public API ------------------------------------------------------------- */

export function reportError(report) {
  enqueue(report || {});
}

export function reportCaughtError(err, component, extras) {
  enqueue({
    message: (err && err.message) || String(err),
    stack: err && err.stack,
    component,
    severity: "fatal",
    context: extras,
  });
}

let installedConsoleError = null;
let installedConsoleWarn = null;

export function installBugReporter() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  try {
    window.addEventListener("error", (e) => {
      reportError({ message: (e && e.message) || "window.error", stack: e && e.error && e.error.stack, source: "client" });
    });
    window.addEventListener("unhandledrejection", (e) => {
      const r = e && e.reason;
      reportError({ message: (r && r.message) || String(r) || "unhandledrejection", stack: r && r.stack, source: "client" });
    });

    // Intercept console.error/warn — but skip our own '[BugReporter]' logs so we never
    // self-feed an infinite loop.
    const origError = console.error.bind(console);
    const origWarn = console.warn.bind(console);
    installedConsoleError = origError;
    installedConsoleWarn = origWarn;
    console.error = (...args) => {
      try {
        const msg = args.map(String).join(" ");
        if (!msg.startsWith(PREFIX)) reportError({ message: msg, severity: "error", source: "client" });
      } catch { /* ignore */ }
      origError(...args);
    };
    console.warn = (...args) => {
      try {
        const msg = args.map(String).join(" ");
        if (!msg.startsWith(PREFIX)) reportError({ message: msg, severity: "warning", source: "client" });
      } catch { /* ignore */ }
      origWarn(...args);
    };

    setInterval(flush, FLUSH_MS);
    window.addEventListener("pagehide", flushBeacon);
    document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") flushBeacon(); });
  } catch (e) {
    // installation must never crash the app
    try { (installedConsoleError || console.log)(PREFIX, "install failed:", e && e.message); } catch { /* ignore */ }
  }
}
